import { useCallback } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { Header, HeaderElement } from '@clara/layout-next'
import flow from 'lodash.flow'

import { DebitIcon, SuccessIcon, AttachedInvoiceIcon } from '@clara/icons'
import { Button } from '@clara/ui'
import { useT } from '@clara/locale'

import StepCard from '@/components/step-card'
import HomeGettingStarted from '@/assets/images/home-getting-started.svg'
import { useGlobalStateContext } from '@/store/global/use-global-state'

const GettingStartedPage = ({
  onStartToUseClick,
  isLoading,
}: {
  onStartToUseClick: () => void
  isLoading: boolean
}) => {
  const { t: locize } = useT('reimbursements')
  const { isManagerOrCompanyOwner } = useGlobalStateContext()

  const prefix = useCallback(
    (key: string) => (isManagerOrCompanyOwner ? `owner.${key}` : key),
    [isManagerOrCompanyOwner],
  )

  const t = useCallback(
    (key: string) => flow([prefix, locize])(key),
    [locize, prefix],
  )

  return (
    <>
      <Header>
        <HeaderElement variant="title">{t('main.title')}</HeaderElement>
      </Header>
      <Stack sx={{ mx: 3, my: 1 }} spacing={3}>
        <Stack
          sx={{
            minHeight: '224px',
            background: (theme) => theme.palette.v3.bg.highlight,
            borderRadius: '8px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            overflow: 'hidden',
            position: 'relative',
          }}
          direction="row"
          spacing={4}
        >
          <Stack spacing={2} p={3}>
            <Typography
              variant="h3"
              color={(theme) => theme.palette.v3.content.high_emphasis}
            >
              {t('firststeps.title')}
            </Typography>
            <Typography
              variant="body1"
              color={(theme) => theme.palette.v3.content.high_emphasis}
            >
              {t('firststeps.description')}
            </Typography>
            <Box>
              <Button
                sx={{ minWidth: '180px' }}
                onClick={() => onStartToUseClick()}
                isLoading={isLoading}
              >
                {t('firststeps.cta')}
              </Button>
            </Box>
          </Stack>
          <Stack
            sx={{
              display: { xs: 'none', lg: 'block' },
              img: { width: 'auto', height: '100%', maxWidth: 'unset' },
            }}
          >
            <img
              src={HomeGettingStarted}
              alt="Get your reimbursements quickly and securely"
            />
          </Stack>
        </Stack>
        <Stack spacing={1}>
          <Typography
            variant="subtitle1"
            color={(theme) => theme.palette.v3.content.medium_emphasis}
          >
            {t('firststeps.steps.title')}
          </Typography>
          <Typography
            variant="body1"
            color={(theme) => theme.palette.v3.content.medium_emphasis}
          >
            {t('firststeps.steps.description')}
          </Typography>
        </Stack>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(3, 1fr)',
            },
            gap: 2,
            mt: 3,
          }}
        >
          <StepCard
            icon={<DebitIcon />}
            title={prefix('firststeps.steps.1.title')}
            description={prefix('firststeps.steps.1.description')}
          />
          <StepCard
            icon={
              <SuccessIcon
                sx={(theme) => ({
                  color: theme.palette.v3.primary.active,
                  path: { fill: theme.palette.v3.primary.active },
                })}
              />
            }
            title={prefix('firststeps.steps.2.title')}
            description={prefix('firststeps.steps.2.description')}
          />
          <StepCard
            icon={<AttachedInvoiceIcon />}
            title={prefix('firststeps.steps.3.title')}
            description={prefix('firststeps.steps.3.description')}
          />
        </Box>
      </Stack>
    </>
  )
}

export default GettingStartedPage
